import { useEffect, useState } from 'react';
import { Share } from '@capacitor/share';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import { Button, Stack } from '@mui/material';
import { PictureSendCode } from '../../../../assets/pictures';
import CopyToClipboard from '../../../atoms/CopyToClipboard';
import LayoutInit from '../../../layouts/templates/LayoutInit';
import { LayoutTemplatePicture } from '../../../layouts/templates';

import { postDeviceAccess, setError } from '../../../../redux/slices/clockings';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import CloseButton from '../../clockings/ClockingButtons/ClockingButton/CloseButton';

const InitFromComete = ({ setView, isInModal = false }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { code, error } = useSelector((state) => state.clockings);
  const [counter, setCounter] = useState(600);
  const [canShare, setCanShare] = useState(false);
  const pagename = "Initialiser l'appareil";

  useEffect(() => {
    const load = async () => {
      dispatch(postDeviceAccess());
      const share = await Share.canShare();
      setCanShare(share.value);
    };

    load();
  }, [isInModal]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Une erreur est survenue, veuillez réessayer ultérieurement.', {
        variant: 'error',
        action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
      });
      setError(false);
    }
  }, [error]);

  useEffect(() => {
    let timer;
    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [counter]);

  if (isInModal) {
    return <FormInit code={code} counter={counter} canShare={canShare} />;
  }

  return (
    <LayoutInit pagename={pagename} handleClickBack={() => setView('')}>
      <FormInit code={code} counter={counter} canShare={canShare} />
    </LayoutInit>
  );
};

InitFromComete.propTypes = {
  setView: PropTypes.func,
  isInModal: PropTypes.bool
};

const FormInit = (props) => {
  const { code, canShare, counter } = props;
  return (
    <LayoutTemplatePicture
      picture={<PictureSendCode />}
      title="Code à transmettre"
      subtitle="Transmettez le code d'association à votre administrateur"
      action={<Actions code={code} canShare={canShare} />}
    >
      <CopyToClipboard code={code || ''} label="Code d'association" counter={counter} sx={{ my: 2 }} />
    </LayoutTemplatePicture>
  );
};

FormInit.propTypes = {
  code: PropTypes.string,
  canShare: PropTypes.bool,
  counter: PropTypes.number
};

const Actions = (props) => {
  const { code, canShare } = props;
  const { isLargeMobile } = useBreakpoints();

  const handleClickRefresh = () => {
    window.location.pathname = `${window.env.PUBLIC_URL}/pointages`;
  };

  const handleClickShare = async () => {
    await Share.share({
      title: "Partagez le code d'association à votre administrateur",
      text: code
    });
  };

  return (
    <Stack sx={styleContainer(isLargeMobile)}>
      {canShare && (
        <Button
          variant="contained"
          size="large"
          disabled={!code}
          color="comete"
          sx={styleButtons(isLargeMobile)}
          onClick={handleClickShare}
        >
          Partager
        </Button>
      )}

      <Button
        variant="outlined"
        size="large"
        color="comete"
        sx={styleButtons(isLargeMobile)}
        onClick={handleClickRefresh}
      >
        Actualiser
      </Button>
    </Stack>
  );
};

const styleContainer = (isLargeMobile) => ({
  flexDirection: isLargeMobile ? 'column' : 'row-reverse',
  gap: 2,
  flexGrow: 1,
  alignItems: isLargeMobile ? 'stretch' : 'end',
  justifyContent: isLargeMobile ? 'end' : 'stretch',
  height: '100%'
});

const styleButtons = (isLargeMobile) => ({
  flexGrow: isLargeMobile ? 0 : 1
});

Actions.propTypes = {
  code: PropTypes.string,
  canShare: PropTypes.bool
};

export default InitFromComete;
